<template>
    <layout-authenticated>
        <v-breadcrumbs :items="breadcrumb"></v-breadcrumbs>
        <h1 class="mb-3">{{ $t('summary.summary') }}</h1>
        <!-- order items -->
        <h3 class="mb-3 grey--text">{{ $t('summary.shopping_cart_items') }}</h3>
        <shopping-cart-component :user-id="userId" :warehouse-id="warehouseId" readonly class="mb-10"/>
        <!-- recipients -->
        <h3 class="mb-3 grey--text">{{ $t('summary.recipients') }}</h3>
        <recipients-table :user-id="userId" :warehouse-id="warehouseId" readonly class="mb-10"/>
        <!-- optional message -->
        <v-row>
            <v-col>
                <h3 class="mb-3 grey--text">{{ $t('summary.order_message') }}</h3>
                <v-textarea
                    v-model="form.message"
                    :label="$t('summary.message')"
                    rows="3"
                    outlined
                    dense
                />
            </v-col>
        </v-row>
        <!-- confirm button -->
        <div class="d-flex mt-3 justify-end">
            <v-btn color="primary"
                   class="mr-3"
                   :disabled="recipientsCount === 0 || isBusy"
                   @click="onSubmitOrder"
                   :loading="isBusy"
            >
                <v-icon left>mdi-checkbox-marked-circle-outline</v-icon>
                {{ $t('summary.submit_order') }}
            </v-btn>
        </div>
    </layout-authenticated>
</template>

<script>
import RecipientsTable from '@/components/user/recipients/RecipientsTable'
import ShoppingCartComponent from '@/components/user/shoppingCart/ShoppingCartComponent'
import SnackbarMixin from '@/mixins/SnackbarMixin'
import ShoppingCartMixin from '@/mixins/ShoppingCartMixin'
import BulkOrderMixin from '@/mixins/BulkOrderMixin'
import {API} from '@/api/api'
import LayoutAuthenticated from "@/layouts/LayoutAuthenticated";
import {
    aggregateProducts,
    findUnavailableProducts,
    formatUnavailableProductsAlert,
    snapshotProducts,
    snapshotRecipients,
    snapshotShoppingCartData
} from "@/utils/orderAvailability";

export default {
    name: "Summary",
    mixins: [SnackbarMixin, ShoppingCartMixin, BulkOrderMixin],
    components: {LayoutAuthenticated, ShoppingCartComponent, RecipientsTable},
    data: () => ({
        // loading
        isBusySubmitOrder: false,
        isBusyCheckAvailability: false,
        // form
        form: {
            message: ""
        }
    }),
    computed: {
        isBusy() {
            return this.isBusySubmitOrder || this.isBusyCheckAvailability;
        },
        userId() {
            return this.$store.getters['identity/getUserId'];
        },
        warehouseId() {
            return this.$store.getters['warehouse/getWarehouseId'](this.userId);
        },
        recipientsCount() {
            let userId = this.$store.getters['identity/getUserId'];
            if (userId === null) {
                this.snackbarError(this.$t('snackbar.identity_check_failed'));
                return;
            }

            let warehouseId = this.$store.getters['warehouse/getWarehouseId'](userId);
            if (warehouseId === null) {
                this.snackbarError(this.$t('snackbar.warehouses.not_selected'));
                return;
            }

            return this.getRecipientsInCart(userId, warehouseId).length;
        },
        breadcrumb() {
            return [
                {
                    text: this.$t('app.name'),
                    href: '/'
                },
                {
                    text: this.$t('shopping_cart.shopping_cart'),
                    href: '/cart'
                },
                {
                    text: this.$t('summary.summary'),
                    href: '/summary',
                    disabled: true
                }
            ];
        }
    },
    methods: {
        async onSubmitOrder() {
            if (this.isBusy) {
                return;
            }

            this.isBusyCheckAvailability = true;
            try {
                const userId = this.userId;
                const warehouseId = this.warehouseId;
                const products = snapshotProducts(
                    this.getProductsInCart(userId, warehouseId)
                );
                const recipients = snapshotRecipients(
                    this.getRecipientsInCart(userId, warehouseId)
                );
                const message = this.form.message;
                const shoppingCartData = JSON.stringify(snapshotShoppingCartData(
                    userId,
                    warehouseId,
                    products,
                    recipients
                ));
                const items = Object.freeze(products.map(product => Object.freeze({
                    name: product.name,
                    external_id: product.external_id,
                    brand: product.brand,
                    model: product.model,
                    image_url: product.image_url,
                    quantity: product.quantity
                })));
                const orders = Object.freeze(recipients.map(recipient => Object.freeze({
                    buyer_external_id: recipient.buyer_id,
                    address_external_id: recipient.address_id,
                    phone: recipient.phone,
                    email: recipient.email,
                    warehouse_id: warehouseId,
                    items,
                    message,
                    shopping_cart_data: shoppingCartData
                })));

                const availability = aggregateProducts(
                    recipients.map(() => products)
                );
                const unavailableProducts = await findUnavailableProducts(
                    warehouseId,
                    availability,
                    API
                );
                if (unavailableProducts.length > 0) {
                    window.alert(formatUnavailableProductsAlert(
                        this.$t("summary.check_availability_failure"),
                        unavailableProducts
                    ));
                    return;
                }

                this.isBusySubmitOrder = true;
                this.isBusyCheckAvailability = false;
                for (const order of orders) {
                    await API.post("/api/orders", order);
                }

                this.removeProductsFromCart(userId, warehouseId);
                this.snackbarSuccess(this.$t('snackbar.order.created'));
                this.$router.push({name: "orders"});
            } catch (error) {
                this.snackbarError(
                    typeof error === "string"
                        ? error
                        : (error.message || this.$t("snackbar.unknown_error"))
                );
            } finally {
                this.isBusyCheckAvailability = false;
                this.isBusySubmitOrder = false;
            }
        }
    }
}
</script>
